import Fuse from 'fuse.js'

export const searchAliases: [string, string][] = [[' und ', ' + ']]

/*
 * getAlternativeTerms
 *
 * This function will check a search term agains a list of known aliases
 * and return a list of alternative search terms generated from the aliases.
 *
 * Ex.:
 * There is a known alias for ' und ' -> ' + '
 * Given the term 'R und V', this function will return ['R + V']
 *
 * @param searchTerm: string
 */
const getAlternativeTerms = (searchTerm: string): string[] => {
  return searchAliases.reduce<string[]>((alternativeTerms, [matcher, replacement]) => {
    const regex = new RegExp(matcher, 'i')

    if (regex.test(searchTerm)) {
      return [...alternativeTerms, searchTerm.replace(regex, replacement)]
    }

    return alternativeTerms
  }, [])
}

type GetSuggestionsOptions<T = unknown> = {
  fuse: Fuse<T, Fuse.FuseOptions<T>>
  /* A function to return a key of an option. This key should be unique */
  getOptionKey: (option: T) => string | number
}

/*
 * getSuggestions
 *
 * This function will find suggestions for a searchTerm and its aliases
 */
const getSuggestions = <T = unknown>(searchTerm: string, config: GetSuggestionsOptions<T>): T[] => {
  const { fuse, getOptionKey } = config

  const alternativeTerms = [searchTerm, ...getAlternativeTerms(searchTerm)]

  return alternativeTerms.reduce<T[]>((suggestions, currentSearchTerm): T[] => {
    const previousSuggestionsKeys = suggestions.map(getOptionKey)
    const newSuggestions = fuse.search(currentSearchTerm) as Fuse.FuseResultWithScore<T>[]
    // For more details of why we need to match EPSILON see: https://github.com/krisk/Fuse/issues/545
    const exactMatch = newSuggestions.find(({ score }) => score === Number.EPSILON)

    if (exactMatch) return [exactMatch.item]

    const newUniqueSuggestions = newSuggestions
      .filter((suggestion) => previousSuggestionsKeys.indexOf(getOptionKey(suggestion.item)) === -1)
      .map(({ item }) => item)

    return [...suggestions, ...newUniqueSuggestions]
  }, [])
}

export const insuranceFieldHelpers = {
  getAlternativeTerms,
  getSuggestions,
}
